import { useEffect, useRef } from 'react';

function getScrollParent(
  element: HTMLElement | null | undefined,
): HTMLElement | null {
  if (!element) {
    return null;
  }

  if (element.scrollTop > 0) {
    return element;
  }

  return getScrollParent(element.parentElement);
}

interface UseScrollToTopOptions {
  // When true, skips the very first effect run so that a full-page reload
  // (e.g. pull-to-refresh) does not fire scrollTop = 0 mid-animation and
  // interrupt the iOS endRefreshing() restore of contentInset.
  // Do NOT set this on components that use the key-remount pattern to scroll
  // to top on navigation (e.g. <ScrollToTop key={page} />).
  skipInitialMount?: boolean;
}

export function useScrollToTop(
  anchorElement?: HTMLElement | null,
  deps: unknown[] = [],
  { skipInitialMount = false }: UseScrollToTopOptions = {},
) {
  const isMounted = useRef(false);

  useEffect(() => {
    if (skipInitialMount && !isMounted.current) {
      isMounted.current = true;
      return;
    }

    const scrollableElement = getScrollParent(anchorElement);

    if (scrollableElement) {
      scrollableElement.scrollTop = 0;
    } else {
      document.body.scrollTop = document.documentElement.scrollTop = 0;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorElement, ...deps]);
}

export function ScrollToTop({
  anchorElement,
}: {
  anchorElement?: HTMLElement | null;
}) {
  useScrollToTop(anchorElement);

  return null;
}
